<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ArrowLeft } from '@lucide/vue'
import { Badge, Button, Card, Modal, PageHeader, Spinner } from '@drama/ui'

import { getMediaAsset } from '../api/asset-library.js'
import { formatBytes, formatDate } from './intent-video/format.js'

const props = defineProps({
  id: { type: String, required: true },
})

const { t } = useI18n()
const router = useRouter()

const loading = ref(true)
const error = ref('')
const item = ref(null)

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await getMediaAsset(props.id)
    item.value = data.item
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function back() {
  router.back()
}

/** 只有这四种在 assetLibrary.type.* 里有译名；这张表历史上还写过 npc/reference/asset/ui/scene/player 这些，原样显示。 */
const KNOWN_TYPES = ['image', 'video', 'audio', 'prompt']
function typeLabel(type) {
  return KNOWN_TYPES.includes(type) ? t(`visualGameAdmin.assetLibrary.type.${type}`) : type
}

const previewKind = computed(() => {
  if (item.value === null) return 'none'
  const probe = item.value.mime || item.value.type || ''
  if (probe.startsWith('video/') || item.value.type === 'video') return 'video'
  if (probe.startsWith('audio/') || item.value.type === 'audio') return 'audio'
  if (probe.startsWith('image/') || item.value.type === 'image') return 'image'
  return item.value.public_url ? 'link' : 'none'
})

const dimensionText = computed(() => (item.value?.width && item.value?.height ? `${item.value.width}×${item.value.height}` : ''))
const metadataText = computed(() => (item.value?.metadata ? JSON.stringify(item.value.metadata, null, 2) : '-'))

const imagePreviewOpen = ref(false)
function openImagePreview() {
  imagePreviewOpen.value = true
}
</script>

<template>
  <div class="mad vga-page" data-insp-path="apps/visual-game-admin/src/pages/MediaAssetDetail.vue">
    <PageHeader :title="t('visualGameAdmin.assetLibrary.detail.title')">
      <template #actions>
        <Button variant="ghost" @click="back">
          <ArrowLeft :size="16" /> {{ t('visualGameAdmin.assetLibrary.detail.back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <div v-else-if="item === null" class="vga-state">
      <p>{{ t('visualGameAdmin.assetLibrary.detail.notFound') }}</p>
    </div>
    <template v-else>
      <Card data-insp-path="apps/visual-game-admin/src/pages/MediaAssetDetail.vue#preview">
        <template #header>
          <div class="mad__head">
            <Badge size="sm">{{ typeLabel(item.type) }}</Badge>
            <h2 class="mad__tag">{{ item.tag }}</h2>
          </div>
        </template>

        <div class="mad__preview">
          <video v-if="previewKind === 'video'" :src="item.public_url" controls playsinline preload="metadata" class="mad__video" />
          <audio v-else-if="previewKind === 'audio'" :src="item.public_url" controls class="mad__audio" />
          <button v-else-if="previewKind === 'image'" type="button" class="mad__thumb-btn" @click="openImagePreview">
            <img :src="item.public_url" class="mad__thumb" :alt="item.tag" />
          </button>
          <a v-else-if="previewKind === 'link'" :href="item.public_url" target="_blank" rel="noopener" class="mad__open-link">
            {{ t('visualGameAdmin.assetLibrary.allMedia.open') }}
          </a>
          <span v-else class="mad__no-preview">-</span>
        </div>
        <div v-if="dimensionText || item.size || item.mime" class="mad__preview-meta">
          <span v-if="dimensionText">{{ dimensionText }}</span>
          <span v-if="item.size">{{ formatBytes(item.size) }}</span>
          <span v-if="item.mime">{{ item.mime }}</span>
        </div>
      </Card>

      <section class="mad__grid">
        <Card data-insp-path="apps/visual-game-admin/src/pages/MediaAssetDetail.vue#basic">
          <template #header>{{ t('visualGameAdmin.assetLibrary.detail.basicInfo') }}</template>
          <dl class="mad__kv">
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.id') }}</dt><dd class="mad__mono mad__wrap">{{ item.id }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.table.tag') }}</dt><dd class="mad__mono mad__wrap">{{ item.tag }}</dd></div>
            <div>
              <dt>{{ t('visualGameAdmin.assetLibrary.allMedia.pack') }}</dt>
              <dd>{{ item.pack_id ? `${item.pack_title}（${item.pack_id}）` : t('visualGameAdmin.assetLibrary.allMedia.global') }}</dd>
            </div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.draftId') }}</dt><dd class="mad__mono">{{ item.draft_id || '-' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.createdAt') }}</dt><dd>{{ formatDate(item.created_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.updatedAt') }}</dt><dd>{{ formatDate(item.updated_at) }}</dd></div>
          </dl>
        </Card>

        <Card data-insp-path="apps/visual-game-admin/src/pages/MediaAssetDetail.vue#storage">
          <template #header>{{ t('visualGameAdmin.assetLibrary.detail.storageInfo') }}</template>
          <dl class="mad__kv">
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.mime') }}</dt><dd class="mad__mono">{{ item.mime || '-' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.size') }}</dt><dd>{{ formatBytes(item.size) }}{{ dimensionText ? ` · ${dimensionText}` : '' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.objectKey') }}</dt><dd class="mad__mono mad__wrap">{{ item.object_key || '-' }}</dd></div>
            <div>
              <dt>{{ t('visualGameAdmin.assetLibrary.allMedia.publicUrl') }}</dt>
              <dd class="mad__mono mad__wrap">
                <a v-if="item.public_url" :href="item.public_url" target="_blank" rel="noopener">{{ item.public_url }}</a>
                <template v-else>-</template>
              </dd>
            </div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.allMedia.hash') }}</dt><dd class="mad__mono mad__wrap">{{ item.hash || '-' }}</dd></div>
          </dl>
        </Card>
      </section>

      <Card data-insp-path="apps/visual-game-admin/src/pages/MediaAssetDetail.vue#metadata">
        <template #header>{{ t('visualGameAdmin.assetLibrary.allMedia.metadata') }}</template>
        <pre class="mad__metadata">{{ metadataText }}</pre>
      </Card>
    </template>

    <Modal :open="imagePreviewOpen" width="min(90vw, 960px)" @update:open="imagePreviewOpen = $event">
      <img :src="item?.public_url" class="mad__preview-image" alt="" />
    </Modal>
  </div>
</template>

<style scoped>
.mad { display: grid; gap: var(--drama-space-xl); }
.mad__head { display: flex; align-items: center; gap: var(--drama-space-sm); }
.mad__tag { margin: 0; overflow-wrap: anywhere; font-size: var(--drama-font-size-md); font-family: var(--drama-font-family-mono, monospace); }
.mad__preview { display: flex; align-items: center; justify-content: center; min-height: 160px; padding: var(--drama-space-lg); border-radius: var(--drama-radius-md); background: var(--drama-color-bg); }
.mad__video { width: 100%; max-height: 520px; border-radius: var(--drama-radius-sm); background: #000; }
.mad__audio { width: 100%; max-width: 480px; }
.mad__thumb-btn { padding: 0; border: none; background: none; cursor: zoom-in; }
.mad__thumb { max-width: 100%; max-height: 520px; border-radius: var(--drama-radius-sm); }
.mad__no-preview { color: var(--drama-color-text-muted); }
.mad__open-link { color: var(--drama-color-primary); }
.mad__preview-meta { display: flex; flex-wrap: wrap; justify-content: center; gap: var(--drama-space-md); margin-top: var(--drama-space-md); color: var(--drama-color-text-muted); font-family: var(--drama-font-family-mono, monospace); font-size: var(--drama-font-size-xs); }
.mad__preview-image { display: block; max-width: 100%; max-height: 80vh; margin: 0 auto; border-radius: var(--drama-radius-sm); }
.mad__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-xl); }
.mad__kv { display: grid; gap: var(--drama-space-md); margin: 0; }
.mad__kv div { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.mad__kv dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.mad__kv dd { margin: 0; font-size: var(--drama-font-size-sm); word-break: break-all; }
.mad__kv dd a { color: var(--drama-color-primary); }
.mad__mono { font-family: var(--drama-font-family-mono, monospace); }
.mad__wrap { white-space: pre-wrap; }
.mad__metadata { margin: 0; padding: var(--drama-space-md); background: var(--drama-color-surface-alt); border-radius: var(--drama-radius-sm); font-family: var(--drama-font-family-mono, monospace); font-size: var(--drama-font-size-xs); overflow-x: auto; white-space: pre-wrap; word-break: break-all; }
@media (max-width: 760px) {
  .mad__grid { grid-template-columns: 1fr; }
}
</style>
